import { type Registry as Services, service } from '@ember/service';
import { underscore } from '@ember/string';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationAdapter from 'lumen/adapters/application';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';
import type { Promise as RsvpPromise } from 'rsvp';

export default class CurrencyConversionExchangeRatesProfileAdapter extends ApplicationAdapter {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  // @ts-expect-error: Property 'buildURL' in type is not assignable to the same property in base type.
  buildURL(
    modelName: string,
    id: string,
    snapshot: Snapshot<unknown>,
    requestType: string,
    query?: Record<string, unknown>,
  ): string {
    // @ts-expect-error: Expected 3 arguments, but got 5.
    const baseUrl = super.buildURL(modelName, id, snapshot, requestType, query);

    const url = new URL(baseUrl, this.host);
    const { hotel, market } = this.hotelAndMarketService;
    if (hotel) {
      url.searchParams.set('subscription_id', hotel.id!);
    }
    if (market) {
      url.searchParams.set('market_id', market.id!);
    }

    return url.toString();
  }

  /**
   * Custom updateRecord to send only changed attributes in PATCH requests.
   * Pass `adapterOptions: { includeRates: true }` to also include the full
   * rates array (e.g. when the user explicitly saves exchange rates).
   *
   * @param store
   * @param schema
   * @param snapshot
   * @returns
   */
  updateRecord(
    store: Store,
    schema: ModelSchema,
    snapshot: Snapshot,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): RsvpPromise<any> {
    const url = this.buildURL(
      schema.modelName,
      snapshot.id ?? '',
      snapshot,
      'updateRecord',
    );

    const serializedSnapshot = snapshot.serialize({ includeId: true });
    const changedAttrs = snapshot.changedAttributes();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const payloadKey = store
      .serializerFor(snapshot.modelName)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .payloadKeyFromModelName(snapshot.modelName);

    const data = {
      [payloadKey]: {},
    };

    const shouldIncludeRates =
      snapshot.adapterOptions?.['includeRates'] === true;
    const keysToUpdate: string[] = shouldIncludeRates
      ? Array.from(new Set([...Object.keys(changedAttrs), 'rates']))
      : Object.keys(changedAttrs);

    keysToUpdate.forEach((key: string) => {
      const decamelizedKey = underscore(key);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      data[payloadKey][decamelizedKey] = serializedSnapshot[decamelizedKey];
    });

    // @ts-expect-error: Incorrect type
    return this.ajax(url, 'PATCH', { data });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  urlForCreateRecord(
    _modelName: string,
    snapshot: Snapshot<CurrencyConversionExchangeRatesProfile>,
  ) {
    if (!snapshot.record) {
      return '';
    }
    return '/apigateway/v1/app/finance/exchange-rate-profiles/';
  }

  urlForDeleteRecord(id: string | number): string {
    return `/apigateway/v1/app/finance/exchange-rate-profiles/${id}`;
  }

  urlForFindRecord(id: string | number): string {
    return `/apigateway/v1/app/finance/exchange-rate-profiles/${id}`;
  }

  urlForUpdateRecord(id: string | number): string {
    return `/apigateway/v1/app/finance/exchange-rate-profiles/${id}`;
  }
}
